(() => {
  const body = document.body;

  if (!body || !body.hasAttribute('data-lazy-image')) {
    return;
  }

  const trackedImages = new WeakSet();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mediaSelector = '.media, [data-image-skeleton]';
  const imageSelector = `:is(${mediaSelector}) img:is([src], [srcset])`;
  const activeClass = 'image-skeleton-active';

  const getMedia = (image) => image.closest(mediaSelector);

  // Off-screen skeletons. Both loops are infinite CSS animations and the
  // shimmer animates background-position, which only runs on the main
  // thread, so while any skeleton exists the page restyles and repaints
  // every frame - even for lazy images several screens away. A skeleton is
  // paused only while its .media is more than one viewport height above or
  // below the visible area (vertical distance only, so the clipped slides of
  // a visible slider and off-canvas drawers keep running), and it is resumed
  // with its original startTime before it can be seen: same timeline, same
  // start, so it shows exactly the frame it would have shown had it never
  // been paused.
  //
  // The same goes for a skeleton in a horizontal scroller (product gallery,
  // card shelves) that is more than half the scroller's width beyond its
  // visible edge: lazy slides further along on a phone used to loop forever
  // behind the clip. Sliders only move by scrolling, and a scroll event runs
  // before the frame that shows the new position, so these wake the same
  // way; a tap or key press (a variant pick can reflow the slides without a
  // scroll) also wakes them first, and they sleep again once things are
  // quiet.
  const skeletonAnimationNames = ['apple-image-skeleton-breathe', 'apple-image-skeleton-shimmer'];
  const pausedSkeletons = new WeakMap(); // media -> [[animation, startTime]]
  const touchedSkeletons = new Map(); // media -> animations ever paused (see endTouchedSkeletons)
  const watchedMedia = new WeakSet();
  const hiddenMedia = new WeakSet(); // not rendered: display: none, skipped content-visibility
  const outsideMedia = new Set(); // rendered, but outside the observer's one-viewport margin
  const farMedia = new Set();
  const sideMedia = new Set(); // far media only paused for being beyond their scroller's edge
  const scrollers = new WeakMap(); // media -> nearest horizontal scroller, or null
  const observeQueue = new Set();
  const pauseQueue = new Set();
  let idleScheduled = false;
  let listening = false;

  const scrollerOf = (media) => {
    if (!scrollers.has(media)) {
      let scroller = null;

      for (let element = media.parentElement; element && element !== body; element = element.parentElement) {
        const overflowX = getComputedStyle(element).overflowX;

        if (overflowX === 'auto' || overflowX === 'scroll') {
          scroller = element;
          break;
        }
      }

      scrollers.set(media, scroller);
    }

    return scrollers.get(media);
  };

  const isFar = (media) => {
    const rect = media.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) {
      sideMedia.delete(media);
      return true;
    }

    const scroller = scrollerOf(media);
    const box = scroller?.getBoundingClientRect();
    const side = !!box && box.width > 0
      && (rect.left > box.right + box.width / 2 || rect.right < box.left - box.width / 2);

    if (side) {
      sideMedia.add(media);
    } else {
      sideMedia.delete(media);
    }

    return side;
  };

  // CSSAnimation getters flush pending style, so every read happens before
  // the first write.
  const wake = (mediaList) => {
    const toResume = [];

    mediaList.forEach((media) => {
      if (!farMedia.delete(media)) {
        return;
      }

      sideMedia.delete(media);

      (pausedSkeletons.get(media) || []).forEach(([animation, startTime]) => {
        if (animation.playState === 'paused') {
          toResume.push([animation, startTime]);
        }
      });
      pausedSkeletons.delete(media);
    });

    toResume.forEach(([animation, startTime]) => {
      animation.startTime = startTime;
    });
  };

  const pauseFarSkeletons = () => {
    const mediaList = [...pauseQueue].filter((media) => farMedia.has(media) && outsideMedia.has(media));
    const near = [];
    const toPause = [];
    const pending = [];

    pauseQueue.clear();

    mediaList.forEach((media) => {
      if (!isFar(media)) {
        near.push(media);
        return;
      }

      media.getAnimations({ subtree: true }).forEach((animation) => {
        if (animation.effect?.target !== media || !skeletonAnimationNames.includes(animation.animationName) || animation.playState !== 'running') {
          return;
        }

        if (animation.startTime === null) {
          pending.push([media, animation]);
        } else {
          toPause.push([media, animation, animation.startTime]);
        }
      });
    });

    toPause.forEach(([media, animation, startTime]) => {
      const paused = pausedSkeletons.get(media) || [];
      const touched = touchedSkeletons.get(media) || new Set();

      paused.push([animation, startTime]);
      pausedSkeletons.set(media, paused);
      touched.add(animation);
      touchedSkeletons.set(media, touched);
      animation.pause();
    });

    wake(near);

    // An animation that has not started yet has no startTime to come back to.
    pending.forEach(([media, animation]) => {
      animation.ready.then(() => queuePause(media), () => {});
    });
  };

  const idleWork = () => {
    idleScheduled = false;
    observeQueue.forEach((media) => skeletonObserver.observe(media));
    observeQueue.clear();
    pauseFarSkeletons();
  };

  // Watching and pausing are never urgent, and both measure layout or read
  // animation state (which flushes style), so they wait for idle time after
  // the page has loaded instead of landing in the middle of page-load work.
  // A skeleton whose image arrives before then is never watched at all.
  const scheduleIdleWork = () => {
    if (idleScheduled) {
      return;
    }

    idleScheduled = true;

    const request = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(idleWork, { timeout: 1000 });
      } else {
        setTimeout(idleWork, 200);
      }
    };

    if (document.readyState === 'complete') {
      request();
    } else {
      window.addEventListener('load', request, { once: true });
    }
  };

  const queuePause = (media) => {
    pauseQueue.add(media);
    scheduleIdleWork();
  };

  const sleep = (media) => {
    if (!farMedia.has(media)) {
      farMedia.add(media);
      queuePause(media);
    }
  };

  // Scroll and resize events run before the frame that shows the new
  // position is painted, so even a jump of several viewports (End key,
  // anchor link, a scroller's scrollTo) resumes a skeleton before it can be
  // seen. Only media outside the observer's margin can be far away.
  const checkOutsideMedia = () => {
    const near = [];

    outsideMedia.forEach((media) => {
      if (isFar(media)) {
        sleep(media);
      } else {
        near.push(media);
      }
    });

    wake(near);
  };

  let quietTimer = 0;

  const wakeSideMedia = () => {
    if (!sideMedia.size) {
      return;
    }

    wake([...sideMedia]);
    clearTimeout(quietTimer);
    quietTimer = setTimeout(checkOutsideMedia, 2000);
  };

  const skeletonObserver = 'IntersectionObserver' in window && typeof Element.prototype.getAnimations === 'function'
    ? new IntersectionObserver((entries) => {
      const near = [];

      entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
        const rendered = boundingClientRect.width > 0 || boundingClientRect.height > 0;

        if (isIntersecting || rendered) {
          hiddenMedia.delete(target);
        } else {
          hiddenMedia.add(target);
        }

        if (isIntersecting || !rendered) {
          outsideMedia.delete(target);
          near.push(target);
        } else {
          outsideMedia.add(target);

          if (isFar(target)) {
            sleep(target);
          } else {
            near.push(target);
          }
        }
      });

      wake(near);

      if (outsideMedia.size && !listening) {
        listening = true;
        document.addEventListener('scroll', checkOutsideMedia, { capture: true, passive: true });
        window.addEventListener('resize', checkOutsideMedia, { passive: true });
        document.addEventListener('pointerdown', wakeSideMedia, { capture: true, passive: true });
        document.addEventListener('keydown', wakeSideMedia, { capture: true, passive: true });
      }
    }, { rootMargin: '100%' })
    : null;

  // Once a CSS animation has been paused through the API, Chromium no longer
  // cancels it when its animation-name goes away while the pseudo-element
  // itself stays (a thumbnail's ::before/::after is also its dot/selection
  // ring), so it would keep running after the skeleton ended. Whenever the
  // style ends the loops - skeleton classes removed, media removed, reduced
  // motion switched on - the ones this script touched are ended with it.
  const endTouchedSkeletons = (media) => {
    (touchedSkeletons.get(media) || []).forEach((animation) => animation.cancel());
    touchedSkeletons.delete(media);
  };

  // Called once the skeleton classes are gone or the media left the page.
  const forget = (media) => {
    if (!watchedMedia.has(media)) {
      return;
    }

    watchedMedia.delete(media);
    hiddenMedia.delete(media);
    outsideMedia.delete(media);
    farMedia.delete(media);
    sideMedia.delete(media);
    observeQueue.delete(media);
    pauseQueue.delete(media);
    pausedSkeletons.delete(media);
    endTouchedSkeletons(media);
    skeletonObserver.unobserve(media);
  };

  // A skeleton restarts when its media is rendered again (display: none, a
  // skipped content-visibility section): measure it again, or pause it
  // again if it is still far away.
  if (skeletonObserver) {
    document.addEventListener('animationstart', ({ target, animationName }) => {
      if (!watchedMedia.has(target) || !skeletonAnimationNames.includes(animationName)) {
        return;
      }

      if (farMedia.has(target)) {
        queuePause(target);
      } else if (hiddenMedia.has(target)) {
        hiddenMedia.delete(target);
        skeletonObserver.unobserve(target);
        skeletonObserver.observe(target);
      }
    });

    reduceMotion.addEventListener?.('change', () => {
      if (reduceMotion.matches) {
        [...touchedSkeletons.keys()].forEach(endTouchedSkeletons);
      }
    });
  }

  const hasImageWithState = (media, state) => {
    return Array.from(media.querySelectorAll(`img[data-image-load-state='${state}']`)).some(
      (image) => getMedia(image) === media
    );
  };

  const finish = (image, state) => {
    const media = getMedia(image);

    if (!media) {
      return;
    }

    // Only fade in an image that was actually being held hidden (skeleton
    // showing, or the media still carrying .loading, which keeps the img at
    // opacity 0). This script is deferred, so begin() also reaches images
    // that already finished loading and are already on screen (cached or
    // eager above-the-fold images whose lazy-image already cleared
    // .loading). Replaying a 0 -> 1 fade on those made them blink out and
    // back in right after first paint.
    const wasHidden = image.dataset.imageLoadState === 'loading'
      || media.classList.contains(activeClass)
      || media.classList.contains('loading');

    image.dataset.imageLoadState = state;
    if (media.closest('hero-lineup:not([data-images-ready])')) return;
    media.removeAttribute('data-skeleton-pending');

    if (!hasImageWithState(media, 'loading')) {
      media.classList.remove(activeClass, 'loading');
      forget(media);
    }

    media.classList.toggle('image-load-error', hasImageWithState(media, 'error'));

    if (state === 'loaded' && wasHidden && !media.closest('hero-lineup') && !reduceMotion.matches && typeof image.animate === 'function') {
      image.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        {
          duration: 320,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
        }
      );
    }
  };

  const begin = (image) => {
    const media = getMedia(image);

    if (!media) {
      return;
    }


    if (!trackedImages.has(image)) {
      trackedImages.add(image);
      image.addEventListener('load', () => finish(image, 'loaded'));
      image.addEventListener('error', () => finish(image, 'error'));
    }

    if (image.complete && image.naturalWidth > 0) {
      finish(image, 'loaded');
      return;
    }
    if (image.complete && image.currentSrc) {
      finish(image, 'error');
      return;
    }

    image.dataset.imageLoadState = 'loading';
    media.classList.remove('image-load-error');
    media.classList.add(activeClass);

    // Reduced motion has no loops to pause.
    if (skeletonObserver && !reduceMotion.matches && !watchedMedia.has(media)) {
      watchedMedia.add(media);
      observeQueue.add(media);
      scheduleIdleWork();
    }
  };

  const scan = (root) => {
    if (root instanceof HTMLImageElement && root.matches(imageSelector)) {
      begin(root);
    }

    if (root instanceof Element || root instanceof Document) {
      root.querySelectorAll(imageSelector).forEach(begin);
    }
  };

  scan(document);
  document.addEventListener('image-skeleton:ready', (event) => scan(event.target));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        begin(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => scan(node));

      // Stop watching skeletons removed while still loading (e.g. a
      // re-rendered drawer), so they are not kept alive.
      if (skeletonObserver) {
        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof Element) || node.isConnected) {
            return;
          }

          if (node.matches(mediaSelector)) {
            forget(node);
          }

          node.querySelectorAll(mediaSelector).forEach(forget);
        });
      }
    });
  }).observe(body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset']
  });
})();
